<script setup lang="ts">
import InputCardComponent from '@/components/InputCardComponent.vue';
import { useAgreementStore } from '@/stores/agreement';
import { useConfigStore } from '@/stores/config';
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { addYears, eachYearOfInterval, endOfMonth, isWithinInterval, startOfMonth, subYears } from 'date-fns';
import BottomBarComponent from '@/components/BottomBarComponent.vue';
import BackComponent from '@/components/BackComponent.vue';
import NextComponent from '@/components/NextComponent.vue';

const { agreement, agreementType } = storeToRefs(useAgreementStore())
const config = useConfigStore().config

const serviceDateMin = ref<Date>(startOfMonth(subYears(new Date(), 2)))
const serviceDateMax = ref<Date>(endOfMonth(addYears(new Date(), 2)))
const monthPickerLabel = computed(() => {
  return agreement.value.nextServicePaymentMethod === 'include' ? 'Hvornår var seneste service?' : 'Hvornår er næste service?'
})

function getMonth(index: number): string {
  //const date = new Date().setMonth(index)
  const date = new Date(2000, index, 1)
  return new Intl.DateTimeFormat('da-DK', { month: 'long' }).format(date)
}

function isValidMonth(index: number): boolean {
  const { startYear } = agreement.value

  if (!startYear) return false

  const interval = {
    start: serviceDateMin.value,
    end: serviceDateMax.value
  }

  //const monthToCheck = new Date(startYear, index - 0)
  const monthToCheck = new Date(startYear, index, 1)

  return isWithinInterval(monthToCheck, interval)
}

watch(() => agreement.value.nextServicePaymentMethod, (value) => {
  if (value) {
    switch (value) {
      case 'include':
        serviceDateMin.value = startOfMonth(subYears(new Date(), 2))
        serviceDateMax.value = endOfMonth(new Date())
        break;
        
      case 'cash':
        serviceDateMin.value = startOfMonth(new Date())
        serviceDateMax.value = endOfMonth(addYears(new Date(), 2))
        break;

      default:
        break;
    }
  }
}, { immediate: true })

function onPaymentMethodChange() {
  agreement.value.startYear = undefined
  agreement.value.startMonth = undefined
}
</script>

<template>
  <v-container>
    <v-row>
      <v-col>
        <InputCardComponent label="Fra hvornår gælder aftalen?">
          <v-radio-group v-model="agreement.nextServicePaymentMethod" @update:model-value="onPaymentMethodChange" hide-details>
            <v-radio label="Fra sidste service- eller leveringstidspunkt." value="include"></v-radio>
            <v-radio label="Fra et fremtidigt service- eller leveringstidspunkt" value="cash"></v-radio>
          </v-radio-group>
        </InputCardComponent>
      </v-col>
    </v-row>
    <v-slide-x-transition>
      <v-row v-if="agreement.nextServicePaymentMethod">
        <v-col>
          <InputCardComponent
            :label="monthPickerLabel"
          >
            <v-row>
              <v-col>
                <v-btn-toggle
                  v-model="agreement.startYear"
                  class="w-100"
                  variant="flat"
                >
                  <v-btn
                    v-for="year in eachYearOfInterval({ start: serviceDateMin, end: serviceDateMax })"
                    :value="year.getFullYear()"
                    class="flex-grow-1"
                  >
                    {{ year.getFullYear() }}
                  </v-btn>
                </v-btn-toggle>
              </v-col>
            </v-row>
            <v-slide-y-transition>
              <v-row v-show="agreement.startYear">
                <v-col>
                  <v-row no-gutters>
                    <v-col
                      v-for="n in 12"
                      :key="n"
                      cols="4"
                    >
                      <v-btn
                        class="w-100 text-capitalize"
                        variant="text"
                        :disabled="!isValidMonth(n - 1)"
                        :active="agreement.startMonth === n - 1"
                        @click="agreement.startMonth = n - 1"
                      >
                        {{ getMonth(n - 1) }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-slide-y-transition>
          </InputCardComponent>
        </v-col>
      </v-row>
    </v-slide-x-transition>
  </v-container>

  <BottomBarComponent>
    <BackComponent to="/agreement/details" />
    <NextComponent
      to="/agreement/addons"
      :disabled="!agreement.startYear || typeof agreement.startMonth !== 'number'"
    />
  </BottomBarComponent>
</template>

<style>
.month-wrapper {
  display: flex;
  grid-template-columns: repeat(1fr, 4);
}

.month-button {

}
</style>
